import { Activities, Lead, User } from '.';
import { ActivitiesMethods } from 'common/methods';
import { action, computed, observable, reaction } from 'mobx';
import { app } from './../App';

// Interfaces
import {
  IActivity,
  IActivities,
  IActivitiesInfo,
  IActivitiesGroup,
} from 'common/interfaces/activity';
import { UserType } from 'common/interfaces/user';

export class LeadActivities {
  constructor(
    private user: User,
    private lead: Lead,
    private activities: Activities
  ) {}

  // React to open lead change
  private openLeadChangeReaction = reaction(
    () => this.lead.openLeadId,
    (openLeadId: number) => {
      if (openLeadId !== null) {
        if (this.leadActivitiesForSalesPerson === null) {
          this.setLeadActivitiesForSalesPerson(
            openLeadId,
            this.user.userType === UserType.SalesManager ? -1 : this.user.id,
            this.user.userType === UserType.SalesManager
              ? 'All'
              : this.user.name || this.user.username
          );
        }

        this.fetchLeadActivities(openLeadId);
      }
    }
  );

  // React to completed activities change
  private showCompletedActivitiesReaction = reaction(
    () => this.showCompletedActivities,
    (showFinished: boolean) => {
      if (this.lead.openLeadId !== null) {
        this.fetchLeadActivities(this.lead.openLeadId);
      }
    }
  );

  @observable public showActivitiesForSalesPerson: Array<{
    leadId: number;
    salesPersonId: number;
    salesPersonName: string;
  }> = [];

  @computed public get leadActivitiesForSalesPerson(): {
    salesPersonId: number;
    salesPersonName: string;
  } | null {
    let id = -1;
    let name = 'All';

    if (
      typeof this.showActivitiesForSalesPerson !== 'undefined' &&
      this.showActivitiesForSalesPerson
    ) {
      this.showActivitiesForSalesPerson.some((activity) => {
        if (this.lead.openLeadId === activity.leadId) {
          id = activity.salesPersonId;
          name = activity.salesPersonName;
          return true;
        }
      });
    }

    return { salesPersonId: id, salesPersonName: name };
  }

  @action public setLeadActivitiesForSalesPerson(
    leadId: number,
    salesPersonId: number,
    salesPersonName: string
  ): boolean {
    let found: boolean = false;

    if (!salesPersonId) {
      return false;
    }

    this.showActivitiesForSalesPerson.some((activity) => {
      if (activity.leadId === leadId) {
        activity.salesPersonId = salesPersonId;
        activity.salesPersonName = salesPersonName;
        found = true;
        return true;
      }
    });

    if (found === false) {
      this.showActivitiesForSalesPerson.push({
        leadId,
        salesPersonId,
        salesPersonName,
      });
    }

    return true;
  }

  @observable public showCompletedActivities: boolean = false;

  @observable public leadActivitiesLoading: boolean = false;
  @observable public leadActivities: IActivities[] = [];

  @computed public get openLeadActivitiesIndex(): number {
    let leadIndex = -1;
    this.leadActivities.some((lead, i) => {
      if (lead.id === this.lead.openLeadId) {
        leadIndex = i;
        return true;
      }
    });

    return leadIndex;
  }

  @observable public showActivitiesOfOtherLeads: boolean = false;

  @action public toggleShowingOfActivitiesOfOtherLeads = (show?: boolean) => {
    show =
      typeof show !== 'undefined' ? show : !this.showActivitiesOfOtherLeads;
    this.showActivitiesOfOtherLeads = show;
  };

  @computed public get activitiesGroupsOfOtherLeads(): IActivitiesGroup[] {
    const otherLeadsActivities: IActivity[] = [];

    if (this.activities.userActivitiesIndex !== -1) {
      this.activities.userActivities[
        this.activities.userActivitiesIndex
      ].activities.map((userActivity) => {
        if (
          typeof userActivity.providerId !== 'undefined' &&
          userActivity.providerId !== this.lead.openLeadId
        ) {
          otherLeadsActivities.push(userActivity);
        }
      });
    }

    return ActivitiesMethods.parseActivitiesGroups(otherLeadsActivities);
  }

  @computed public get openLeadActivitiesIds(): number[] {
    if (this.openLeadActivitiesIndex !== -1) {
      return this.leadActivities[this.openLeadActivitiesIndex].activities.map(
        (activity, i) => activity.id
      );
    } else {
      return [];
    }
  }

  @computed public get activitiesLeadLength(): number {
    return this.openLeadActivitiesIndex !== -1
      ? this.leadActivities[this.openLeadActivitiesIndex].activities.length
      : 0;
  }

  @computed private get openLeadAllActivities(): IActivity[] {
    // Get all activities allowed to be seen by logged in user
    let activities: IActivity[] =
      typeof this.leadActivities[this.openLeadActivitiesIndex] !== 'undefined'
        ? this.leadActivities[this.openLeadActivitiesIndex].activities || []
        : [];

    // Filter activities by user filter
    if (
      this.leadActivitiesForSalesPerson &&
      this.leadActivitiesForSalesPerson.salesPersonId &&
      this.leadActivitiesForSalesPerson.salesPersonId > 0
    ) {
      activities = activities.filter(
        (activity) =>
          activity.assignedToUserId ===
          this.leadActivitiesForSalesPerson.salesPersonId
      );
    }

    // Filter activities by quantity to show
    if (this.showCompletedActivities && this.user.maxFinishedActivitiesCount) {
      activities = activities.slice(
        0,
        this.user.maxFinishedActivitiesCount || 999999999
      );
    }

    return activities;
  }

  @computed public get openLeadAllActivitiesCount(): number {
    return this.openLeadAllActivities.length;
  }

  @computed public get activitiesGroups(): IActivitiesGroup[] {
    return ActivitiesMethods.parseActivitiesGroups(this.openLeadAllActivities);
  }

  // Fetch lead activities
  @action public fetchLeadActivities = async (
    providerId: number
  ): Promise<IActivity[]> => {
    // Indicate that activities are loading
    this.leadActivitiesLoading = true;

    // Get assigned to user to fetch
    const finished: boolean = this.showCompletedActivities;

    const activitiesQuery: any = {
      providerId,
      finished: finished ? 1 : 0,
      $limit: 99999999,
      $sort: {
        activityTime: finished ? -1 : 1,
      },
      $skip: 0,
    };

    if (this.user && this.user.userType !== UserType.SalesManager) {
      activitiesQuery.assignedToUserId = this.user.id;
    }

    try {
      // Get all activities visible to the user
      const activitiesService: any = app.service('activity');
      const activitiesInfo = (await activitiesService.find({
        query: activitiesQuery,
      })) as IActivitiesInfo;

      // Check if open lead activities are already fetched
      let leadIndex = -1;
      this.leadActivities.some((lead, i) => {
        if (lead.id === this.lead.openLeadId) {
          leadIndex = i;
          return true;
        }
      });

      // Update state
      if (leadIndex !== -1) {
        this.leadActivities = this.leadActivities
          .slice(0, leadIndex)
          .concat({
            id: this.lead.openLeadId,
            activities: activitiesInfo.data.map((activity, i) => ({
              ...activity,
              savingComplete: false,
            })),
          })
          .concat(this.leadActivities.slice(leadIndex + 1));
      } else {
        this.leadActivities.push({
          id: this.lead.openLeadId,
          activities: activitiesInfo.data.map((activity, i) => ({
            ...activity,
            savingComplete: false,
          })),
        });
      }

      return activitiesInfo.data;
    } catch (err) {
      console.error(err);
      return err;
    } finally {
      this.leadActivitiesLoading = false;
    }
  };
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./app/web/src/stores/LeadActivities.ts